import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Stack,
  Grid,
  Autocomplete,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Card,
  CardContent,
  IconButton,
  Chip,
  capitalize,
} from '@mui/material';
import { Check, RemoveRedEyeOutlined, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { StyledCellHeader } from '../../../../components/StyledCellHeader';
import { formatNumberWithCommas } from '../../../../utils/generalFunctions';
import AccessDeniedBox from '../../../../components/AccessDeniedBox';



function StatusChip({ status }) {
  let color = 'default';
  if (status === 'Activo') color = 'success';
  else if (status === 'Pendiente') color = 'warning';
  else if (status === 'Cancelado') color = 'error';
  else if (status === 'Irrecuperable') color = 'warning';
  else if (status === 'Pagado') color = 'success';
  
  return <Chip label={status} color={color} textTransform="none" size='small'/>;
}

const IrrecuperablesLoans = () => {
  const navigate = useNavigate();
  const [loanData, setLoanData] = useState([]);
  const [loanDataFiltered, setLoanDataFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  useEffect(() => {
    fetchIrrecuperablesLoans();
  }, []);

  const fetchIrrecuperablesLoans = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/prestamos/${localStorage.getItem('idcompany')}?estado=irrecuperable`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });
      const data = await response.json();
      if(data["error"]!="Acceso denegado"){
        setAccessDenied(false);
        setLoanData(Array.isArray(data) ? data.filter(loan => loan.producto != "") : []);
        setLoanDataFiltered(Array.isArray(data) ? data.filter(loan => loan.producto != "") : []);
      } else {
        setAccessDenied(true);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching irrecuperables loans:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (val) => {
    if (val != null) {
      setLoanDataFiltered(Array.isArray(loanData) ? loanData.filter(loan => loan.loan_id == val.loan_id) : []);
    } else {
      setLoanDataFiltered(loanData);
    }
  };

  const handleMarkAsRecoverable = async (loanId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/prestamos/${loanId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify({
          estado: 'activo'
        })
      });

      if (!response.ok) {
        throw new Error('Error al marcar el préstamo como recuperable');
      }

      // Refresh the data
      fetchIrrecuperablesLoans();
      
    } catch (error) {
      console.error('Error marking loan as recoverable:', error);
    }
  };

  // Calculate KPIs
  const totalIrrecuperables = loanData.length;
  const totalAmount = loanData.reduce((acc, loan) => acc + parseFloat(loan.monto_aprobado || 0), 0);
  const averageAmount = totalIrrecuperables > 0 ? totalAmount / totalIrrecuperables : 0;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Cargando préstamos irrecuperables...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    !accessDenied ? 
    <>
      {/* Summary Cards */}
      <Grid container spacing={2} px={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ backgroundColor: '#fff3e0' }}>
            <CardContent>
              <Typography variant="subtitle1" color="warning.main">Total Irrecuperables</Typography>
              <Typography variant="h5" fontWeight="bold" color="warning.main">
                {totalIrrecuperables}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ backgroundColor: '#ffebee' }}>
            <CardContent>
              <Typography variant="subtitle1" color="error.main">Monto Total Perdido</Typography>
              <Typography variant="h5" fontWeight="bold" color="error.main">
                ${formatNumberWithCommas(totalAmount.toFixed(2))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ backgroundColor: '#f3e5f5' }}>
            <CardContent>
              <Typography variant="subtitle1" color="secondary.main">Promedio por Préstamo</Typography>
              <Typography variant="h5" fontWeight="bold" color="secondary.main">
                ${formatNumberWithCommas(averageAmount.toFixed(2))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Box sx={{ mt: 4 }}>
        <Autocomplete
          options={loanData}
          getOptionLabel={(option) => {return option.loan_id+" - "+option.cliente_nombre+" - "+option.producto_financiero}}    
          onChange={(e, val) => {handleInputChange(val)}}
          renderInput={(params) => <TextField {...params} placeholder="Buscar Préstamo Irrecuperable" fullWidth />}
          sx={{ mb: 3, bgcolor: "#f0f2f5", borderRadius: 2 }}
          fullWidth
        />
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none' }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledCellHeader>ID Préstamo</StyledCellHeader>
              <StyledCellHeader>Cliente</StyledCellHeader>
              <StyledCellHeader>Producto Financiero</StyledCellHeader>
              <StyledCellHeader>Monto Aprobado</StyledCellHeader>
              <StyledCellHeader>Estado</StyledCellHeader>
              <StyledCellHeader>Sucursal</StyledCellHeader>
              <StyledCellHeader>Fecha de Creación</StyledCellHeader>
              <StyledCellHeader>Acciones</StyledCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {loanDataFiltered.length > 0 ? (
              loanDataFiltered?.length > 0 ? loanDataFiltered.map((loan) => (
                <TableRow key={loan.id}>
                  <TableCell>{loan.loan_id}</TableCell>
                  <TableCell>{loan.cliente_nombre}</TableCell>
                  <TableCell>{loan.producto_financiero}</TableCell>
                  <TableCell>${formatNumberWithCommas(loan.monto_aprobado)}</TableCell>
                  <TableCell>
                    <StatusChip status={capitalize(loan.estado_prestamo)} />
                  </TableCell>
                  <TableCell>{loan.sucursal_nombre}</TableCell>
                  <TableCell>{loan.fecha_de_creacion}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <IconButton 
                        size='small' 
                        onClick={()=>{navigate(`/gestion_comercial/prestamos/detalle/${loan.id}`)}}
                        title="Ver Detalles"
                      >
                        <RemoveRedEyeOutlined />
                      </IconButton>
                      <IconButton 
                        size='small' 
                        color="success"
                        onClick={() => handleMarkAsRecoverable(loan.id)}
                        title="Marcar como Recuperable"
                      >
                        <Check />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              )) : [] 
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No se encontraron préstamos irrecuperables
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary Footer */}
      <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Mostrando {loanDataFiltered?.length} de {loanData?.length} préstamos irrecuperables
        </Typography>
      </Box>
    </>
    :
    <AccessDeniedBox
      title={'Acceso denegado'}
      message={'No tienes permisos para ver esta sección de Irrecuperables. Contacta a tu administrador para solicitar acceso o verifica tus roles y permisos.'}
    />
  );
};

export default IrrecuperablesLoans;
